import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { kaReducer, Table } from 'ka-table';
import { DataType, SortingMode } from 'ka-table/enums';
import { GlobalSearch } from "../Marketing Compliance/GlobalSearch";

export default function AssignmentsTable({ assignments }) {
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState('');
    const [filteredData, setFilteredData] = useState([]);

    const [tableProps, setTableProps] = useState({
        columns: [
            { key: 'rowIndex', title: '#', dataType: DataType.Number },
            { key: 'Subject', title: 'Subject', dataType: DataType.String },
            { key: 'Priority', title: 'Priority', dataType: DataType.String },
            { key: 'Status', title: 'Status', dataType: DataType.String },
            { key: 'ActivityDate', title: 'Due Date', dataType: DataType.String },
            { key: 'chevron', title: '', dataType: DataType.String }
        ],
        data: [],
        rowKeyField: 'id',
        sortingMode: SortingMode.Single,
        paging: {
            enabled: true,
            pageIndex: 0,
            pageSize: 10,
            pageSizes: [10, 20, 30],
        },
    });

    const columns = useMemo(() => tableProps.columns, [tableProps.columns]);

    // Format assignments data and update column titles
    useEffect(() => {
        if (Array.isArray(assignments) && assignments.length > 0) {
            // Update column titles from the first assignment's labels
            const firstAssignment = assignments[0];
            const updatedColumns = [
                { key: 'rowIndex', title: '#', dataType: DataType.Number },
                { key: 'Subject', title: firstAssignment.Subject?.label || 'Subject', dataType: DataType.String },
                { key: 'Priority', title: firstAssignment.Priority?.label || 'Priority', dataType: DataType.String },
                { key: 'Status', title: firstAssignment.Status?.label || 'Status', dataType: DataType.String },
                { key: 'ActivityDate', title: firstAssignment.ActivityDate?.label || 'Due Date', dataType: DataType.String },
                { key: 'chevron', title: '', dataType: DataType.String }
            ];

            const formatted = assignments.map((assignment, idx) => ({
                Id: assignment.Id?.value,
                rowIndex: idx + 1,
                Subject: assignment.Subject?.value,
                Priority: assignment.Priority?.value,
                Status: assignment.Status?.value,
                ActivityDate: assignment.ActivityDate?.value,
                rawData: assignment,
            }));
            setFilteredData(formatted);
            setTableProps((prev) => ({ ...prev, columns: updatedColumns, data: formatted }));
        }
    }, [assignments]);

    // Handle search
    useEffect(() => {
        if (!filteredData.length) return;

        if (!searchText.trim()) {
            setTableProps((prev) => ({ ...prev, data: filteredData }));
        } else {
            const term = searchText.toLowerCase().trim();
            const results = filteredData
                .filter((item) =>
                    columns.some((col) => {
                        const value = item[col.key];
                        return value != null && String(value).toLowerCase().includes(term);
                    })
                )
                .map((item, idx) => ({ ...item, rowIndex: idx + 1 }));
            setTableProps((prev) => ({ ...prev, data: results }));
        }
    }, [searchText, filteredData, columns]);

    const dispatch = (action) => setTableProps((prev) => kaReducer(prev, action));

    const handleAssignmentClick = (assignmentId) => {
        navigate(`/assignment/${assignmentId}`);
    };

    const getPriorityClass = (priority) => {
        switch (priority?.toLowerCase()) {
            case 'high': return 'badge bg-danger';
            case 'medium': return 'badge bg-warning text-dark';
            case 'low': return 'badge bg-success';
            default: return 'badge bg-secondary';
        }
    };

    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed': return 'badge bg-success';
            case 'not started': return 'badge bg-secondary';
            default: return 'badge bg-secondary';
        }
    };

    const EmptyDataComponent = () => (
        <tr>
            <td colSpan={columns.length} className="text-center py-3">
                No assignments found
            </td>
        </tr>
    );

    return (
        <>
            <div className='d-flex justify-content-between align-items-center mt-3'>
                <div style={{ margin: '5px 0 7px' }}>
                    <GlobalSearch onSearchChange={setSearchText} />
                </div>
                <div className="text-muted">
                    Total Assignments: <strong>{assignments.length}</strong>
                </div>
            </div>

            {tableProps.data.length > 0 ? (
                <Table
                    {...tableProps}
                    dispatch={dispatch}
                    childComponents={{
                        cellText: {
                            content: ({ column, rowData, value }) => {
                                if (column.key === 'Subject') {
                                    return <span className="u-row-name">{value}</span>;
                                }
                                if (column.key === 'Priority') {
                                    return <span className={getPriorityClass(value)}>{value}</span>;
                                }
                                if (column.key === 'Status') {
                                    return <span className={getStatusClass(value)}>{value}</span>;
                                }
                                if (column.key === 'ActivityDate') {
                                    return (
                                        <span>
                                            {value ? new Date(value).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                            }) : '-'}
                                        </span>
                                    );
                                }
                                if (column.key === 'chevron') {
                                    return <i className="fa-solid fa-chevron-right u-row-chevron" aria-hidden="true" />;
                                }
                                return <span>{value}</span>;
                            }
                        },
                        table: {
                            elementAttributes: () => ({ className: 'rounded-table' })
                        },
                        tableHead: {
                            elementAttributes: () => ({ className: 'table-header' })
                        },
                        headCell: {
                            elementAttributes: () => ({ className: 'py-1 px-2' })
                        },
                        dataRow: {
                            elementAttributes: (props) => ({
                                className: 'u-row-link',
                                onClick: () => handleAssignmentClick(props.rowData.Id),
                                title: `Open ${props.rowData.Subject ?? 'assignment'}`,
                            })
                        },
                        cell: {
                            elementAttributes: () => ({ className: 'py-2 px-2' })
                        },
                        paging: {
                            elementAttributes: () => ({ className: 'custom-paging' })
                        },
                        pagingPages: {
                            elementAttributes: () => ({ className: 'd-flex justify-content-end' })
                        },
                        pagingIndex: {
                            elementAttributes: props => ({
                                className: `pagination-item ${props.isActive ? 'active' : ''}`
                            })
                        },
                        pagingSizes: {
                            content: ({ dispatch, pageSize }) => (
                                <div className="d-flex align-items-center gap-2">
                                    <span className="text-muted">Rows per page:</span>
                                    <input
                                        type="number"
                                        value={pageSize}
                                        min={1}
                                        onChange={(e) => {
                                            const size = Number(e.target.value) || 1;
                                            dispatch({ type: 'UpdatePageSize', pageSize: size });
                                        }}
                                        className="form-control form-control-sm"
                                        style={{ width: '60px', textAlign: 'center' }}
                                    />
                                </div>
                            )
                        }
                    }}
                />
            ) : (
                <table className="rounded-table">
                    <thead className="table-header">
                        <tr>
                            <th className="py-1 px-2">#</th>
                            <th className="py-1 px-2">{columns[1]?.title || 'Subject'}</th>
                            <th className="py-1 px-2">{columns[2]?.title || 'Priority'}</th>
                            <th className="py-1 px-2">{columns[3]?.title || 'Status'}</th>
                            <th className="py-1 px-2">{columns[4]?.title || 'Activity Date'}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <EmptyDataComponent />
                    </tbody>
                </table>
            )}
        </>
    );
}